import api from '../utils/auth';
import i18n from '../i18n';

const get = async (url, params) => (await api.get(url, { params })).data;
const post = async (url, body) => (await api.post(url, body)).data;
const put = async (url, body) => (await api.put(url, body)).data;
const patch = async (url, body) => (await api.patch(url, body)).data;
const del = async (url) => (await api.delete(url)).data;

const currentLang = () => ((i18n.language || 'ru').slice(0, 2) === 'en' ? 'en' : 'ru');
const withLang = (extra = {}) => ({ ...extra, lang: currentLang() });
const hasCyrillic = (text) => /[А-Яа-яЁё]/.test(text);

/**
 * Текст ошибки для пользователя. Сообщения сервера написаны по-русски: в английском
 * интерфейсе вместо них показываем перевод по коду ответа.
 */
export const errorText = (error, fallback) => {
  const lang = currentLang();
  const status = error?.response?.status;
  const d = error?.response?.data?.detail;
  let serverText = null;
  if (typeof d === 'string') serverText = d;
  else if (Array.isArray(d) && d.length) {
    serverText = d.map((x) => (x.msg || '').replace(/^Value error, /, '')).filter(Boolean).join('; ') || null;
  }
  if (serverText && (lang === 'ru' || !hasCyrillic(serverText))) return serverText;
  if (lang === 'en' && status) {
    const key = status >= 500 ? '5xx' : String(status);
    const translated = i18n.t(`pl.common.errors.${key}`, { defaultValue: '' });
    if (translated) return translated;
  }
  return fallback || i18n.t('pl.common.unknownError');
};

export const platformApi = {
  // публичные
  products: () => get('/products', withLang()),
  product: (slug) => get(`/products/${slug}`, withLang()),
  news: () => get('/news', withLang()),
  newsPost: (slug) => get(`/news/${slug}`, withLang()),
  courses: () => get('/courses', withLang()),
  course: (slug) => get(`/courses/${slug}`, withLang()),
  lesson: (slug, id) => get(`/courses/${slug}/lessons/${id}`, withLang()),
  completeLesson: (slug, id) => post(`/courses/${slug}/lessons/${id}/complete`),
  myCourses: () => get('/me/courses', withLang()),
  sendRequest: (data) => post('/service-requests', data),
  tariffs: () => get('/subscriptions', withLang()),
};

export const platformAdminApi = {
  products: () => get('/admin/products'),
  createProduct: (d) => post('/admin/products', d),
  updateProduct: (id, d) => put(`/admin/products/${id}`, d),
  deleteProduct: (id) => del(`/admin/products/${id}`),
  tariffs: () => get('/admin/tariffs'),
  createTariff: (d) => post('/admin/tariffs', d),
  updateTariff: (id, d) => put(`/admin/tariffs/${id}`, d),

  news: () => get('/admin/news'),
  createNews: (d) => post('/admin/news', d),
  updateNews: (id, d) => put(`/admin/news/${id}`, d),
  publishNews: (id) => post(`/admin/news/${id}/publish`),
  unpublishNews: (id) => post(`/admin/news/${id}/unpublish`),
  deleteNews: (id) => del(`/admin/news/${id}`),

  courses: () => get('/admin/courses'),
  course: (id) => get(`/admin/courses/${id}`),
  createCourse: (d) => post('/admin/courses', d),
  updateCourse: (id, d) => put(`/admin/courses/${id}`, d),
  publishCourse: (id) => post(`/admin/courses/${id}/publish`),
  unpublishCourse: (id) => post(`/admin/courses/${id}/unpublish`),
  deleteCourse: (id) => del(`/admin/courses/${id}`),
  addLesson: (courseId, d) => post(`/admin/courses/${courseId}/lessons`, d),
  updateLesson: (id, d) => put(`/admin/lessons/${id}`, d),
  deleteLesson: (id) => del(`/admin/lessons/${id}`),
  reorderLessons: (courseId, ids) => post(`/admin/courses/${courseId}/lessons/reorder`, { ids }),
  grantCourse: (courseId, email) => post(`/admin/courses/${courseId}/grant`, { email }),

  requests: () => get('/admin/service-requests'),
  updateRequest: (id, d) => patch(`/admin/service-requests/${id}`, d),

  orders: () => get('/admin/orders'),
  paymentEvents: () => get('/admin/payment-events'),
  jobs: () => get('/admin/segmentation-jobs'),
  ledger: (userId) => get(`/admin/users/${userId}/ledger`),

  uploadImage: async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return (await api.post('/admin/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },

  uploadVideo: async (file, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    return (await api.post('/admin/media/video', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0, // большие файлы грузятся дольше стандартного таймаута
      onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
    })).data;
  },
};
